import { boardStore } from "@/lib/boardStore";

function edgePoint(center, other, halfW, halfH) {
  const dx = other.x - center.x;
  const dy = other.y - center.y;
  if (dx === 0 && dy === 0) return { x: center.x, y: center.y };
  const t = 1 / Math.max(Math.abs(dx) / halfW, Math.abs(dy) / halfH);
  return { x: center.x + dx * t, y: center.y + dy * t };
}

export default function YarnLayer({
  connections,
  centers,
  sizes = {},
  cards,
  locked = false,
  onStartPinDrag = () => {},
}) {
  if (!connections.length) return null;

  const points = Object.values(centers);
  if (points.length === 0) return null;
  const allPinPts = connections.flatMap((c) => c.pins || []);
  const allPts = [...points, ...allPinPts];
  const minX = Math.min(...allPts.map((p) => p.x)) - 600;
  const minY = Math.min(...allPts.map((p) => p.y)) - 600;
  const maxX = Math.max(...allPts.map((p) => p.x)) + 600;
  const maxY = Math.max(...allPts.map((p) => p.y)) + 600;
  const width = maxX - minX;
  const height = maxY - minY;

  const toLocal = (pt) => ({ x: pt.x - minX, y: pt.y - minY });

  return (
    <svg
      className="absolute yarn-line"
      style={{
        left: minX,
        top: minY,
        width,
        height,
        overflow: "visible",
        zIndex: 10,
      }}
      viewBox={`0 0 ${width} ${height}`}
    >
      <defs>
        <filter id="yarn-texture" x="-5%" y="-5%" width="110%" height="110%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="1.2"
            numOctaves="2"
            seed="7"
          />
          <feDisplacementMap in="SourceGraphic" scale="1.4" />
        </filter>
      </defs>
      {connections.map((conn) => {
        const cA = centers[conn.a];
        const cB = centers[conn.b];
        if (!cA || !cB) return null;
        const sA = sizes[conn.a] || { w: 220, h: 160 };
        const sB = sizes[conn.b] || { w: 220, h: 160 };
        const pins = conn.pins || [];

        const towardA = pins[0] || cB;
        const towardB = pins[pins.length - 1] || cA;
        const A = edgePoint(cA, towardA, sA.w / 2, sA.h / 2);
        const B = edgePoint(cB, towardB, sB.w / 2, sB.h / 2);

        const path = [A, ...pins, B];
        const color = conn.color || "#DC2626";
        const localPath = path.map(toLocal);
        const d = localPath
          .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
          .join(" ");

        const midIdx = Math.floor((localPath.length - 1) / 2);
        const m1 = localPath[midIdx];
        const m2 = localPath[midIdx + 1] || m1;
        const cx = (m1.x + m2.x) / 2;
        const cy = (m1.y + m2.y) / 2;

        return (
          <g key={conn.id} data-testid={`connection-line-${conn.id}`}>
            <path
              d={d}
              stroke="rgba(0, 0, 0, 0.11)"
              strokeWidth={5}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              transform="translate(2,4)"
              filter="url(#yarn-texture)"
              pointerEvents="none"
            />
            <path
              d={d}
              stroke={color}
              strokeWidth={3}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              filter="url(#yarn-texture)"
              pointerEvents="none"
            />
            <path
              d={d}
              stroke="rgba(255,255,255,0.18)"
              strokeWidth={0.8}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              transform="translate(-0.4,-0.6)"
              pointerEvents="none"
            />

            {!locked &&
              localPath.slice(0, -1).map((p, i) => {
                const q = localPath[i + 1];
                return (
                  <line
                    key={`hit-${i}`}
                    x1={p.x}
                    y1={p.y}
                    x2={q.x}
                    y2={q.y}
                    stroke="transparent"
                    strokeWidth={18}
                    style={{ cursor: "copy" }}
                    pointerEvents="stroke"
                    data-testid={`yarn-segment-${conn.id}-${i}`}
                    onClick={(e) => {
                      const svg = e.currentTarget.ownerSVGElement;
                      const pt = svg.createSVGPoint();
                      pt.x = e.clientX;
                      pt.y = e.clientY;
                      const ctm = svg.getScreenCTM();
                      if (!ctm) return;
                      const local = pt.matrixTransform(ctm.inverse());
                      const worldX = local.x + minX;
                      const worldY = local.y + minY;
                      boardStore.addPin(conn.id, i, worldX, worldY);
                    }}
                  />
                );
              })}

            {pins.map((p, i) => {
              const lp = toLocal(p);
              return (
                <g key={`pin-${i}`}>
                  <circle
                    cx={lp.x}
                    cy={lp.y}
                    r={9}
                    fill="rgba(0,0,0,0.22)"
                    pointerEvents="none"
                  />
                  <circle
                    cx={lp.x - 1}
                    cy={lp.y - 1.5}
                    r={7}
                    fill={color}
                    stroke="rgba(255,255,255,0.35)"
                    strokeWidth={1}
                    data-testid={`yarn-pin-${conn.id}-${i}`}
                    style={{ cursor: locked ? "default" : "grab" }}
                    onMouseDown={(e) => {
                      if (locked) return;
                      e.stopPropagation();
                      onStartPinDrag(conn.id, i);
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      if (locked) return;
                      boardStore.removePin(conn.id, i);
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (locked) return;
                      boardStore.removePin(conn.id, i);
                    }}
                  />
                  <circle
                    cx={lp.x - 2}
                    cy={lp.y - 3}
                    r={2}
                    fill="rgba(255,255,255,0.6)"
                    pointerEvents="none"
                  />
                </g>
              );
            })}

            {conn.label && (
              <foreignObject
                x={cx - 110}
                y={cy - 60}
                width={220}
                height={140}
                style={{ overflow: "visible", pointerEvents: "none" }}
              >
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <div
                    style={{
                      display: "inline-block",
                      maxWidth: "250px",
                      resize: "both",
                      padding: "4px 10px",
                      background: "rgba(240,220,160,0.94)",
                      border: "1px solid rgba(60,40,20,0.45)",
                      borderRadius: 2,
                      fontFamily: "'Special Elite', monospace",
                      fontSize: 30,
                      color: "#2a2018",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      lineHeight: 1.25,
                      textAlign: "center",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.35)",
                    }}
                  >
                    {conn.label}
                  </div>
                </div>
              </foreignObject>
            )}
          </g>
        );
      })}
    </svg>
  );
}
